import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ClockCounterClockwise, MagnifyingGlass, ShieldCheck, XCircle, ArrowsClockwise, Warning } from "@phosphor-icons/react";

const ACTION_LABELS = {
  "document.approved":  { colour: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: ShieldCheck, text: "Approved" },
  "document.rejected":  { colour: "text-red-700 bg-red-50 border-red-200",             icon: XCircle,     text: "Rejected" },
  "document.pending":   { colour: "text-amber-700 bg-amber-50 border-amber-200",       icon: Warning,     text: "Reopened" },
  "document.bulk_approved": { colour: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: ShieldCheck, text: "Bulk approved" },
};

export default function AdminAudit() {
  const [rows, setRows] = useState([]);
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = action !== "all" ? { action } : {};
      const r = await api.get("/admin/audit", { params: { ...params, limit: 200 } });
      setRows(r.data.items || []);
    } catch {
      toast.error("Could not load audit log");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const ql = q.trim().toLowerCase();
    return rows.filter((r) =>
      (r.actor_email || "").toLowerCase().includes(ql)
      || (r.reason || "").toLowerCase().includes(ql)
      || (r.action || "").toLowerCase().includes(ql)
      || (r.target_id || "").toLowerCase().includes(ql)
    );
  }, [rows, q]);

  return (
    <div className="space-y-4" data-testid="admin-audit-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-caps">Compliance</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Audit trail</h1>
          <p className="text-slate-600 mt-1">Every admin decision on documents, users, and other governed entities is logged here.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger data-testid="audit-action-filter" className="h-10 w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="document.approved">Documents approved</SelectItem>
              <SelectItem value="document.rejected">Documents rejected</SelectItem>
              <SelectItem value="document.pending">Documents reopened</SelectItem>
              <SelectItem value="document.bulk_approved">Bulk approvals</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={load} className="text-sm font-semibold text-blue-600 hover:underline" data-testid="audit-refresh">
            <ArrowsClockwise size={14} className="inline mr-1" /> Refresh
          </button>
        </div>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search admin email, reason, or target id…"
          className="pl-9 h-10"
          data-testid="audit-search"
        />
      </div>

      {loading && <div className="card-tactile p-8 text-center text-slate-500">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div className="card-tactile p-10 text-center text-slate-500" data-testid="audit-empty">
          <ClockCounterClockwise size={32} className="mx-auto text-slate-400 mb-2" />
          No audit entries match this filter yet.
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((r) => {
          const meta = ACTION_LABELS[r.action] || {
            colour: "text-slate-700 bg-slate-50 border-slate-200",
            icon: ClockCounterClockwise, text: r.action || "action",
          };
          const Icon = meta.icon;
          return (
            <div key={r.id} className="card-tactile p-4 grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] gap-3 items-start" data-testid={`audit-row-${r.id}`}>
              <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-semibold self-start ${meta.colour}`}>
                <Icon size={12} weight="fill" /> {meta.text}
              </span>
              <div className="min-w-0">
                <div className="text-sm text-slate-900">
                  <b>{r.actor_email || "unknown admin"}</b>
                  <span className="text-slate-500"> · {(r.target_kind || "target")} <span className="font-mono text-xs">{(r.target_id || "").slice(0, 8)}</span></span>
                </div>
                {r.reason && (
                  <div className="mt-1 text-xs text-slate-700 bg-slate-50 border border-slate-100 rounded-md px-2 py-1">
                    "{r.reason}"
                  </div>
                )}
                {r.target_meta && (
                  <div className="mt-1 text-xs text-slate-500 truncate">
                    {Object.entries(r.target_meta).map(([k, v]) => (
                      <span key={k} className="mr-3">{k}: <b>{String(v)}</b></span>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-xs text-slate-500 whitespace-nowrap font-mono">
                {(r.at || "").replace("T", " ").slice(0, 19)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
